import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, FileEdit, ShieldCheck, X } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const CHANGEABLE = ["quote_amount", "guest_count", "date", "period"];
const LABELS = { quote_amount: "Total", guest_count: "Guests", date: "Event date", period: "Time of day" };

function fmtVal(k, v) {
  if (v === null || v === undefined) return "—";
  if (k === "quote_amount") return fmt(v);
  if (k === "date") return new Date(v).toLocaleDateString();
  if (k === "period") return String(v).toUpperCase();
  return String(v);
}

/** Vendor "Propose change" dialog. */
export function ProposeChangeDialog({ booking, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [proposed, setProposed] = useState({});
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setProposed(p => ({ ...p, [k]: v }));
  const submit = async () => {
    if (reason.trim().length < 5) return toast.error("Please add a short reason (at least 5 characters)");
    const cleaned = {};
    Object.entries(proposed).forEach(([k, v]) => {
      if (v === "" || v === undefined || v === null) return;
      if (k === "quote_amount" || k === "guest_count") cleaned[k] = Number(v);
      else cleaned[k] = v;
    });
    if (!Object.keys(cleaned).length) return toast.error("Add at least one change");
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/change-orders`, {
        reason: reason.trim(), proposed: cleaned,
      });
      toast.success("Change order sent to planner");
      setOpen(false); setReason(""); setProposed({});
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button data-testid="propose-change-btn" variant="outline" className="rounded-full w-full h-11 mt-2" onClick={() => setOpen(true)}>
        <FileEdit className="w-4 h-4 mr-2" /> Propose change
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="propose-change-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Propose a change</DialogTitle>
            <DialogDescription className="text-xs text-[#5C5C5C]">
              Only send fields you want to change. The planner will review before and after values, then accept or decline.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="eyebrow mb-1 block">New total (leave blank to keep)</label>
              <Input data-testid="propose-quote" type="number" value={proposed.quote_amount ?? ""} onChange={e => setField("quote_amount", e.target.value)} placeholder={String(booking.quote_amount)} className="bg-white h-11" />
            </div>
            <div>
              <label className="eyebrow mb-1 block">New guest count</label>
              <Input data-testid="propose-guests" type="number" value={proposed.guest_count ?? ""} onChange={e => setField("guest_count", e.target.value)} placeholder={String(booking.guest_count)} className="bg-white h-11" />
            </div>
            <div>
              <label className="eyebrow mb-1 block">Short reason (required)</label>
              <Textarea data-testid="propose-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Guest count increased by 25" className="bg-white min-h-[80px]" maxLength={500} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">Cancel</Button>
            <Button data-testid="propose-submit-btn" onClick={submit} disabled={busy} className="btn-sage rounded-full">
              {busy ? "Sending…" : "Send change order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/** Diff row for a single field. */
function DiffRow({ field, diff }) {
  return (
    <div className="grid grid-cols-[110px,1fr,20px,1fr] gap-2 items-center py-2 border-b border-[#EFEADF] last:border-0 text-sm" data-testid={`diff-row-${field}`}>
      <div className="eyebrow">{LABELS[field] || field}</div>
      <div className="line-through text-[#5C5C5C]">{fmtVal(field, diff.before)}</div>
      <ArrowRight className="w-3.5 h-3.5 text-[color:var(--text-muted)]" />
      <div className="font-serif text-base">{fmtVal(field, diff.after)}</div>
    </div>
  );
}


/** Planner-facing review+sign / decline dialog for a pending change order. */
export function ReviewChangeOrderDialog({ changeOrder, defaultName = "", onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("review"); // "review" | "sign" | "decline"
  const [name, setName] = useState(defaultName);
  const [affirmed, setAffirmed] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    if (!affirmed) return toast.error("Please affirm you agree to the change");
    if ((name || "").trim().length < 2) return toast.error("Type your full name to sign");
    setBusy(true);
    try {
      const { data } = await api.post(`/change-orders/${changeOrder.change_order_id}/accept`,
        { signed_name: name.trim(), affirmed: true });
      if (data.refund_review_required) {
        toast.info("Accepted. NContro admin will review the potential refund.");
      } else {
        toast.success("Change order accepted");
      }
      setOpen(false); onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/change-orders/${changeOrder.change_order_id}/decline`,
        { reason: declineReason.trim() });
      toast.success("Change order declined");
      setOpen(false); onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const diff = changeOrder.diff || {};
  const changedFields = Object.keys(diff);

  return (
    <>
      <Button data-testid="review-change-order-btn" className="btn-sage rounded-full w-full h-11" onClick={() => { setOpen(true); setMode("review"); }}>
        Review change order
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setMode("review"); setAffirmed(false); setDeclineReason(""); } }}>
        <DialogContent data-testid="review-co-dialog" className="max-w-lg bg-[#FDFBF7] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {mode === "sign" ? "Sign the updated agreement" : mode === "decline" ? "Decline change order" : "Review change order"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C5C5C]">
              {mode === "review" && "Below are the fields your vendor is proposing to change. If you accept, a new signed agreement version supersedes the current one — but the original stays on file."}
              {mode === "sign" && "Sign to lock in the new terms."}
              {mode === "decline" && "Your current agreement stays in effect. Optionally tell the vendor why."}
            </DialogDescription>
          </DialogHeader>

          {mode === "review" && (
            <div className="space-y-3 text-sm mt-2">
              <div className="rounded-md bg-white border border-[#E8E2D9] p-4">
                {changedFields.map(f => <DiffRow key={f} field={f} diff={diff[f]} />)}
              </div>
              <div className="rounded-md bg-[#F5F2EB] border border-[#E8E2D9] p-3 text-[13px]">
                <div className="eyebrow mb-1">Vendor's reason</div>
                <div>{changeOrder.reason}</div>
              </div>
            </div>
          )}

          {mode === "sign" && (
            <div className="space-y-3 text-sm mt-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" data-testid="co-affirm-checkbox" checked={affirmed} onChange={e => setAffirmed(e.target.checked)} className="mt-1" />
                <span className="text-[13px] leading-snug">
                  I have reviewed the changes and agree to the updated booking terms shown above.
                </span>
              </label>
              <div>
                <label className="eyebrow mb-1 block">Sign by typing your full name</label>
                <Input data-testid="co-signed-name" value={name} onChange={e => setName(e.target.value)} className="bg-white h-11" placeholder="e.g. Jane Doe" maxLength={120} />
              </div>
            </div>
          )}

          {mode === "decline" && (
            <div className="space-y-3 text-sm mt-2">
              <Textarea data-testid="co-decline-reason" value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Optional — helps the vendor propose a better change" className="bg-white min-h-[100px]" maxLength={500} />
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {mode === "review" && (
              <>
                <Button variant="ghost" onClick={() => setMode("decline")} className="rounded-full text-[#C05A46]">
                  <X className="w-4 h-4 mr-1" /> Decline
                </Button>
                <Button onClick={() => setMode("sign")} className="btn-sage rounded-full" data-testid="proceed-to-sign-btn">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Accept & sign
                </Button>
              </>
            )}
            {mode === "sign" && (
              <>
                <Button variant="ghost" onClick={() => setMode("review")} disabled={busy} className="rounded-full">Back</Button>
                <Button onClick={accept} disabled={busy} className="btn-sage rounded-full" data-testid="co-accept-submit-btn">
                  {busy ? "Signing…" : "Sign & accept"}
                </Button>
              </>
            )}
            {mode === "decline" && (
              <>
                <Button variant="ghost" onClick={() => setMode("review")} disabled={busy} className="rounded-full">Back</Button>
                <Button onClick={decline} disabled={busy} className="rounded-full bg-[#C05A46] hover:bg-[#A34D3B] text-white" data-testid="co-decline-submit-btn">
                  {busy ? "Declining…" : "Decline change"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/** Compact agreement-history strip shown on the booking detail. */
export function ChangeOrderHistory({ bookingId, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/change-orders`)
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [bookingId, refreshKey]);

  if (loading || items.length === 0) return null;
  return (
    <div className="card-elegant p-5" data-testid="change-order-history">
      <div className="eyebrow mb-3">Change order history</div>
      <ul className="space-y-2 text-sm">
        {items.map(co => (
          <li key={co.change_order_id} className="flex items-center justify-between gap-3 border-b border-[#EFEADF] last:border-0 pb-2" data-testid={`co-history-${co.change_order_id}`}>
            <div>
              <div className="font-serif text-base">
                Proposed {Object.keys(co.diff || {}).map(k => LABELS[k] || k).join(", ")}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-[#5C5C5C]">
                {new Date(co.created_at).toLocaleDateString()} · {co.status}
                {co.resulting_agreement_version && ` · v${co.resulting_agreement_version}`}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
